import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Trash, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import StoryComposer from "./StoryComposer";
import { StoryTags } from "@/pages/Stories";
import { track } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Avatar = ({ name, picture, size = "w-6 h-6" }) =>
  picture ? (
    <img src={picture} alt={name} className={`${size} rounded-full object-cover`} />
  ) : (
    <div
      className={`${size} rounded-full bg-[#2D302A] text-white flex items-center justify-center text-[10px] uppercase`}
      style={{ fontFamily: "IBM Plex Mono, monospace" }}
    >
      {(name || "?").charAt(0)}
    </div>
  );

// "Your story" viewer for users who already posted: shows it + lets them delete.
const MyStoryModal = ({ open, onClose, story, onDelete, deleting }) => {
  useBodyScrollLock(open);
  if (!open || !story) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center">
      <div
        className="bg-white w-full max-w-lg rounded-md border border-[#E4E1D9] p-6 md:p-8 max-h-[90vh] overflow-y-auto"
        data-lenis-prevent
        data-testid="my-story-dialog"
      >
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-3xl tracking-tighter" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Your Story
          </h2>
          <button onClick={onClose} className="text-[#6B6A68] hover:text-[#1A1A1A] transition-colors">
            <X size={24} />
          </button>
        </div>
        {story.image_url && (
          <img
            src={`${BACKEND_URL}${story.image_url}`}
            alt="Your story"
            className="w-full max-h-64 object-cover rounded-md border border-[#E4E1D9] mb-4"
          />
        )}
        <div
          className="story-body text-[15px] leading-relaxed text-[#2D302A]"
          dangerouslySetInnerHTML={{ __html: story.content_html }}
        />
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="mt-6 w-full flex items-center justify-center gap-2 border border-red-200 text-red-700 py-3 rounded-md text-xs uppercase tracking-[0.25em] hover:bg-red-50 transition-colors disabled:opacity-60"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="delete-my-story"
        >
          <Trash size={16} /> {deleting ? "Deleting..." : "Delete story"}
        </button>
        <p className="mt-2 text-center text-[11px] text-[#9A9A98]">
          Delete your story to share a new one.
        </p>
      </div>
    </div>
  );
};

// Compact one-row strip for the Men/Women tabs: thumbnail + username on the
// first line, excerpt truncated with an ellipsis on the second.
export const StoryStrip = () => {
  const navigate = useNavigate();
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/stories`, { params: { limit: 8, sort: "shuffle" } });
        if (!cancelled) setPreviews(res.data?.items || []);
      } catch {
        if (!cancelled) setPreviews([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (previews.length === 0) return null;

  return (
    <div className="mb-8 md:mb-12 md:-mt-8" data-testid="story-strip">
      <p
        className="text-[10px] uppercase tracking-[0.3em] text-[#6B6A68] mb-3"
        style={{ fontFamily: "IBM Plex Mono, monospace" }}
      >
        Your Story
      </p>
      <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x pb-1">
        {previews.map((story) => (
          <button
            key={story.id}
            type="button"
            onClick={() => {
              track("story_open", { story_id: story.id, source: "story_strip" });
              navigate(`/stories#story-${story.id}`);
            }}
            className="flex items-center gap-2.5 w-60 shrink-0 snap-start text-left bg-white border border-[#E4E1D9] rounded-md p-2 hover:border-[#C9C5BA] transition-colors"
            data-testid="story-strip-card"
          >
            {story.image_url ? (
              <img
                src={`${BACKEND_URL}${story.image_url}`}
                alt=""
                loading="lazy"
                className="w-11 h-11 rounded object-cover shrink-0"
              />
            ) : (
              <span className="w-11 h-11 rounded shrink-0 bg-[#F0EFEB] flex items-center justify-center">
                <Avatar name={story.user_name} picture={story.user_picture} size="w-7 h-7" />
              </span>
            )}
            <span className="min-w-0">
              <span className="block text-xs text-[#2D302A] truncate">{story.user_name}</span>
              <span className="block text-xs text-[#6B6A68] truncate">{story.excerpt}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const YourStorySection = ({ user }) => {
  const navigate = useNavigate();
  const [previews, setPreviews] = useState([]);
  const [myStory, setMyStory] = useState(null);
  const [showComposer, setShowComposer] = useState(false);
  const [showMine, setShowMine] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Both fetches are best-effort: the section must never crash Discovery.
  const fetchPreviews = useCallback(async () => {
    try {
      // Weekly-shuffled order (reshuffles every 7 days) leads the homepage row.
      const res = await axios.get(`${API}/stories`, { params: { limit: 8, sort: "shuffle" } });
      setPreviews(res.data?.items || []);
    } catch {
      setPreviews([]);
    }
  }, []);

  const fetchMine = useCallback(async () => {
    if (!user) {
      setMyStory(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/stories/mine`, { withCredentials: true });
      setMyStory(res.data?.story || null);
    } catch {
      setMyStory(null);
    }
  }, [user]);

  useEffect(() => {
    fetchPreviews();
  }, [fetchPreviews]);

  useEffect(() => {
    fetchMine();
  }, [fetchMine]);

  const handleDeleteMine = async () => {
    if (!myStory) return;
    if (!window.confirm("Delete your story? This can't be undone.")) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/stories/${myStory.id}`, { withCredentials: true });
      track("story_delete", { story_id: myStory.id, is_own: true, source: "your_story_section" });
      toast.success("Story deleted");
      setShowMine(false);
      setMyStory(null);
      fetchPreviews();
    } catch (err) {
      console.error("Delete story failed:", err);
      toast.error("Could not delete your story");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="mb-8 md:mb-10 md:-mt-12" data-testid="your-story-section">
      <div className="flex items-end justify-between gap-4 mb-3 md:mb-5">
        <div>
          <p
            className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#6B6A68]"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
          >
            Your Story
          </p>
          {myStory && (
            <p className="mt-2 text-[11px] text-[#9A9A98]" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
              You've shared your story
            </p>
          )}
        </div>
      </div>

      {previews.length === 0 ? (
        <p className="text-sm text-[#6B6A68]">Be the first to share yours.</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x pb-1" data-testid="story-previews">
          {previews.map((story) => (
            <button
              key={story.id}
              type="button"
              onClick={() => {
                track("story_open", { story_id: story.id, source: "your_story_section" });
                navigate(`/stories#story-${story.id}`);
              }}
              className="w-60 md:w-72 shrink-0 snap-start text-left bg-white border border-[#E4E1D9] rounded-md overflow-hidden hover:border-[#C9C5BA] transition-colors"
              data-testid="story-preview-card"
            >
              {story.image_url && (
                <img
                  src={`${BACKEND_URL}${story.image_url}`}
                  alt=""
                  loading="lazy"
                  className="w-full aspect-[16/9] md:aspect-[4/3] object-cover"
                />
              )}
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <Avatar name={story.user_name} picture={story.user_picture} />
                  <span className="text-xs text-[#2D302A] truncate">{story.user_name}</span>
                  {story.like_count > 0 && (
                    <span
                      className="ml-auto inline-flex items-center gap-1 text-[10px] text-[#C0392B]"
                      style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    >
                      <Heart size={12} weight="fill" /> {story.like_count}
                    </span>
                  )}
                </div>
                <StoryTags story={story} className="mb-1.5" />
                {/* excerpt is server-computed plain text — safe to render directly */}
                <p className="text-sm text-[#4A4A48] leading-relaxed line-clamp-2 md:line-clamp-3">{story.excerpt}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      <StoryComposer
        open={showComposer}
        onClose={() => setShowComposer(false)}
        onSuccess={() => {
          fetchPreviews();
          fetchMine();
        }}
      />
      <MyStoryModal
        open={showMine}
        onClose={() => setShowMine(false)}
        story={myStory}
        onDelete={handleDeleteMine}
        deleting={deleting}
      />
    </section>
  );
};

export default YourStorySection;
